import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

const cssPrefix = 'AdminProfileDialog';
const borderColor = applyColor(DesignTokens.color.elevation.border.default.subtle, '#e0e0e0');
const secondaryText = applyColor(DesignTokens.color.font.default.secondary, '#666');
const hoverBackground = applyColor(
  DesignTokens.color?.component?.background?.neutral?.default?.non_btn?.hover,
  '#e5e5e5'
);

export namespace AdminProfileDialogStyles {
  export const DialogBody = cssClass(cssPrefix, 'dialog-body', {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  });

  export const Section = cssClass(cssPrefix, 'section', {
    marginBottom: TabStyles.Spacing.MediumSpace,
  });

  export const SectionLabel = cssClass(cssPrefix, 'section-label', {
    fontSize: '13px',
    fontWeight: 500,
    marginBottom: TabStyles.Spacing.SmallSpace,
  });

  export const NameField = cssClass(cssPrefix, 'name-field', {
    width: '100%',
    $nest: { '& input': { width: '100%' } },
  });

  export const DescriptionTextarea = cssClass(cssPrefix, 'description-textarea', {
    width: '100%',
    $nest: {
      '& textarea': {
        width: '100%',
        minHeight: '60px',
        resize: 'vertical',
        boxSizing: 'border-box',
      },
    },
  });

  export const PermissionsContainer = cssClass(cssPrefix, 'permissions-container', {
    border: `1px solid ${borderColor}`,
    borderRadius: '4px',
    maxHeight: '300px',
    overflowY: 'auto',
  });

  // Grey banner for each resource section, no border for a cleaner look
  export const CategoryHeader = cssClass(cssPrefix, 'category-header', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${TabStyles.Spacing.SmallSpace}px ${TabStyles.Spacing.MediumSpace}px`,
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, '#f5f5f5'),
  });

  export const CategoryTitle = cssClass(cssPrefix, 'category-title', {
    fontWeight: 600,
    fontSize: '13px',
  });

  export const CategoryCount = cssClass(cssPrefix, 'category-count', {
    fontSize: '12px',
    color: secondaryText,
    marginLeft: TabStyles.Spacing.SmallSpace,
  });

  export const SelectAll = cssClass(cssPrefix, 'select-all', {
    fontSize: '12px',
    color: '#1976d2',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    fontWeight: 500,
  });

  export const CheckboxList = cssClass(cssPrefix, 'checkbox-list', {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    padding: TabStyles.Spacing.MediumSpace,
    gap: `${TabStyles.Spacing.MediumSpace}px ${TabStyles.Spacing.LargeSpace}px`,
  });

  export const CapabilityItem = cssClass(cssPrefix, 'capability-item', {
    display: 'flex',
    alignItems: 'flex-start',
    gap: TabStyles.Spacing.SmallSpace * 3,
    padding: `${TabStyles.Spacing.SmallSpace}px`,
    margin: `-${TabStyles.Spacing.SmallSpace}px`,
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    $nest: {
      '&:hover': {
        backgroundColor: hoverBackground,
      },
    },
  });

  export const CapabilityContent = cssClass(cssPrefix, 'capability-content', {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  });

  export const CapabilityLabel = cssClass(cssPrefix, 'capability-label', {
    fontWeight: 600,
    fontSize: '13px',
    margin: 0,
  });

  export const CapabilityDescription = cssClass(cssPrefix, 'capability-description', {
    fontSize: '12px',
    color: secondaryText,
    margin: 0,
  });

  export const CapabilityHint = cssClass(cssPrefix, 'capability-hint', {
    fontSize: '11px',
    color: secondaryText,
    margin: 0,
    $nest: {
      '& p': { margin: 0 },
    },
  });

  export const CapabilityMarkdown = cssClass(cssPrefix, 'capability-markdown', {
    $nest: {
      '& p': { margin: 0 },
    },
  });

  export const TotalSelected = cssClass(cssPrefix, 'total-selected', {
    fontSize: '13px',
    color: secondaryText,
    marginTop: TabStyles.Spacing.SmallSpace,
  });

  export const CenterMessage = cssClass(cssPrefix, 'center-message', {
    padding: `${TabStyles.Spacing.LargeSpace}px`,
    textAlign: 'center',
  });

  /** Icon size for capability check/clear icons. 14 matches ClearBaseIcon's supported sizes. */
  export const CapabilityIconSize = 14 as const;

  export const CapabilityIconEnabled = {
    size: CapabilityIconSize,
    color: applyColor(DesignTokens.color.feedback.default.success_dark, '#2e7d32'),
  };

  export const CapabilityIconDisabled = {
    size: CapabilityIconSize,
    color: applyColor(DesignTokens.color.feedback.default.error_dark, '#c62828'),
  };
}
